import React from 'react';
import { useSearchParams } from 'react-router';
import { useSelector } from 'react-redux';
import { z } from 'zod';
import {
  Badge,
  Banner,
  Button,
  Dropdown,
  Flashy,
  Form,
  Grid,
  GridRow,
  GridColumn,
  Gutter,
  Layout,
  LayoutItem,
} from '@axiom/ui';
import type { Opportunity } from '@axiom/validation';
import { OpportunitiesConst } from '@axiom/const';
import { ZodArrayToEnum } from '@axiom/types';

import { OpportunityStore } from '../../stores/opportunity-store';
import { OpportunityLegacyApi } from '../../api/opportunity-legacy-api';
import { DateUtil } from '../../utils/date-util';
import { fullNameByIdSelector } from '../../redux/selectors/users';

const { TransactionSubType } = OpportunitiesConst;

const userOverrideValues = {
  hide: 'hide',
  show: 'show',
  auto: 'auto',
} as const;
const eoiValues = {
  no: 'no',
  yes: 'yes',
};
const formSchema = z.object({
  excludedFromFeedUserOverride: z.enum(
    ZodArrayToEnum(Object.values(userOverrideValues))
  ),
  allowInterest: z.enum(ZodArrayToEnum(Object.values(eoiValues))),
});

type UserOverrideValue =
  (typeof userOverrideValues)[keyof typeof userOverrideValues];
type EoiValue = (typeof eoiValues)[keyof typeof eoiValues];

const effoTranslateForUi = (
  value: Opportunity['excludedFromFeedUserOverride']
) => {
  if (value === true) return userOverrideValues.hide;
  return value === false ? userOverrideValues.show : userOverrideValues.auto;
};
const effoTranslateForApi = (value: UserOverrideValue) =>
  value === userOverrideValues.hide ||
  (value !== userOverrideValues.show && null);

const eoiTranslateForApi = (value?: EoiValue | null) =>
  value === eoiValues.yes || (value !== eoiValues.no && null);

const getAutoVisibilityLabel = (opportunity: Opportunity) => {
  if (opportunity.confidential) {
    return 'Auto-selected: Hidden (this is a confidential opportunity)';
  }
  return opportunity.transactionSubType === TransactionSubType.Backfill
    ? 'Auto-selected: Hidden (this is a backfill opportunity)'
    : 'Auto-selected: Shown (default option)';
};

export const OpportunityFeedVisibilityForm = ({
  opportunity,
}: {
  opportunity: Opportunity;
}) => {
  const [, setSearchParams] = useSearchParams();
  const names = useSelector(fullNameByIdSelector);
  const { positions } = opportunity;

  const emptyPosDesc = !positions[0]?.description?.trim();

  const openPositionDescription = (positionId: string) => {
    setSearchParams(`editPositionDescription=${positionId}`);
  };
  return (
    <Gutter top="36px">
      <Form
        name="OPPORTUNITY_FEED_VISIBILITY_FORM"
        schema={formSchema}
        submitOnChange
        initialValues={{
          excludedFromFeedUserOverride: effoTranslateForUi(
            opportunity.excludedFromFeedUserOverride
          ),
          allowInterest: opportunity.allowInterest
            ? eoiValues.yes
            : eoiValues.no,
        }}
        onSubmit={async (changedData, actions, values) => {
          const allowInterest = eoiTranslateForApi(changedData.allowInterest);

          await OpportunityLegacyApi.updateOpportunity(opportunity.id, {
            excludedFromFeedUserOverride: effoTranslateForApi(
              values.excludedFromFeedUserOverride
            ),
            ...(allowInterest === null ? {} : { allowInterest }),
          });
          await OpportunityStore.load(opportunity.id);
        }}
      >
        {() => {
          const isHiddenFromFeed =
            opportunity.excludedFromFeedUserOverride ??
            opportunity.transactionSubType === TransactionSubType.Backfill;
          return (
            <>
              {!isHiddenFromFeed &&
              positions.length > 0 &&
              positions[0] &&
              !positions[0].description?.trim() ? (
                <Gutter bottom="8px">
                  <Banner
                    impact="low"
                    type="critical"
                    name="ADD_POSITION_BANNER"
                  >
                    <Layout position="middle">
                      <LayoutItem hug>
                        Please add a position description in order to accept
                        expressions of interest.
                      </LayoutItem>
                      <LayoutItem>
                        <Button
                          name="SCROLL_TO_POS_DESC_BUTTON"
                          onClick={() => {
                            openPositionDescription(positions[0]?.id as string);
                          }}
                        >
                          Add Now
                        </Button>
                      </LayoutItem>
                    </Layout>
                  </Banner>
                </Gutter>
              ) : null}
              <Layout horizontalGutter="8px">
                {!!opportunity.confidential && (
                  <LayoutItem bottomGutter="8px">
                    <Badge background="sky" name="CONFIDENTIAL_BADGE">
                      Confidential
                    </Badge>
                  </LayoutItem>
                )}
                {opportunity.transactionSubType ===
                  TransactionSubType.Backfill && (
                  <LayoutItem bottomGutter="8px">
                    <Badge background="sky" name="BACKFILL_BADGE">
                      Backfill
                    </Badge>
                  </LayoutItem>
                )}
                {opportunity.account?.accountType === 'Direct' &&
                  (opportunity.account.denied ? (
                    <LayoutItem bottomGutter="8px">
                      <Badge background="red" name="SELF_SERVICE_DENIED_BADGE">
                        Denied
                      </Badge>
                    </LayoutItem>
                  ) : (
                    <LayoutItem bottomGutter="8px">
                      <Badge background="navy blue" name="SELF_SERVICE_BADGE">
                        Self Service
                      </Badge>
                    </LayoutItem>
                  ))}
              </Layout>
              <Grid columns={2}>
                <GridRow>
                  <GridColumn>
                    <Dropdown
                      name="excludedFromFeedUserOverride"
                      label="Show on Opportunity Feed?"
                      options={[
                        { label: 'Hide', value: userOverrideValues.hide },
                        { label: 'Show', value: userOverrideValues.show },
                        ...((opportunity.excludedFromFeedUserOverride ??
                          null) === null
                          ? [
                              {
                                label: getAutoVisibilityLabel(opportunity),
                                value: userOverrideValues.auto,
                              },
                            ]
                          : []),
                      ]}
                      displayKey="label"
                      valueKey="value"
                    />
                  </GridColumn>
                  <GridColumn>
                    <Dropdown
                      name="allowInterest"
                      label="Accept EOIs?"
                      options={[
                        { label: 'No', value: eoiValues.no },
                        { label: 'Yes', value: eoiValues.yes },
                      ]}
                      displayKey="label"
                      valueKey="value"
                      disabled={emptyPosDesc}
                    />
                  </GridColumn>
                </GridRow>
              </Grid>
              {opportunity.excludedFromFeedUpdatedBy &&
              !!names[opportunity.excludedFromFeedUpdatedBy] &&
              !!opportunity.excludedFromFeedUpdatedAt ? (
                <>
                  <Gutter bottom="8px" />
                  <Flashy
                    name="EXCLUDED_FROM_FEED_USER_OVERRIDE_LAST_UPDATED"
                    color="textSubtle"
                    italic
                  >
                    Last updated{' '}
                    {DateUtil.displayDate(
                      opportunity.excludedFromFeedUpdatedAt
                    )}{' '}
                    by {names[opportunity.excludedFromFeedUpdatedBy]}
                  </Flashy>
                </>
              ) : null}
            </>
          );
        }}
      </Form>
    </Gutter>
  );
};
